import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PACKAGES } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", business: "", package: "", hours: "", details: "" };

const FIELDS = [
  { key: "name", label: "Your name *", placeholder: "Jane Doe" },
  { key: "email", label: "Email address *", placeholder: "you@company.com", type: "email" },
  { key: "phone", label: "Phone", placeholder: "(555) 555-5555", type: "tel" },
  { key: "business", label: "Business name", placeholder: "Your company" },
  { key: "hours", label: "Hours needed per month", placeholder: "e.g. 25" },
];

const inputCls = "w-full bg-transparent py-2.5 outline-none text-base placeholder:opacity-40";
const inputStyle = { borderBottom: "1px solid var(--border-light)", color: "var(--text-primary)" };
const labelCls = "text-xs tracking-[0.2em] uppercase";

export default function QuoteModal({ open, onClose, initialPackage }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) setForm({ ...EMPTY, package: initialPackage || "" });
  }, [open, initialPackage]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.package) {
      toast.error("Please add your name, email and package.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Thank you — your quote request is on its way. We'll be in touch soon.");
      onClose();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong. Please check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="quote-modal"
        className="max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl font-body"
        style={{ backgroundColor: "var(--bg-primary)", border: "1px solid var(--border-light)" }}
      >
        <DialogTitle className="font-serif text-3xl" style={{ color: "var(--text-primary)" }}>
          Request a Quote
        </DialogTitle>
        <DialogDescription style={{ color: "var(--text-secondary)" }}>
          Tell us what you need and we'll send a tailored quote.
        </DialogDescription>
        <form onSubmit={submit} data-testid="quote-form" className="flex flex-col gap-5 mt-2">
          <div>
            <label className={labelCls} style={{ color: "var(--rose-mocha-deep)" }}>Package of interest *</label>
            <select
              data-testid="quote-package-select"
              value={form.package}
              onChange={set("package")}
              className={inputCls}
              style={inputStyle}
            >
              <option value="">Select a package</option>
              {PACKAGES.map((p) => (
                <option key={p.name} value={p.name}>{p.name}</option>
              ))}
              <option value="Custom / Add-ons">Custom package / Add-ons</option>
            </select>
          </div>
          {FIELDS.map((f) => (
            <div key={f.key}>
              <label className={labelCls} style={{ color: "var(--rose-mocha-deep)" }}>{f.label}</label>
              <input
                data-testid={`quote-${f.key}-input`}
                type={f.type || "text"}
                value={form[f.key]}
                onChange={set(f.key)}
                placeholder={f.placeholder}
                className={inputCls}
                style={inputStyle}
              />
            </div>
          ))}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={form.website || ""}
            onChange={set("website")}
            className="hidden"
          />
          <div>
            <label className={labelCls} style={{ color: "var(--rose-mocha-deep)" }}>Details / notes</label>
            <textarea
              data-testid="quote-details-input"
              rows={3}
              value={form.details}
              onChange={set("details")}
              placeholder="Tell us about the tasks you'd like to delegate…"
              className={`${inputCls} resize-none`}
              style={inputStyle}
            />
          </div>
          <button
            type="submit"
            data-testid="quote-submit-button"
            disabled={loading}
            className="self-start rounded-full px-8 py-3.5 text-sm font-medium disabled:opacity-60"
            style={{ backgroundColor: "var(--mocha-brown)", color: "#FBF6F3" }}
          >
            {loading ? "Sending…" : "Send request"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
